/**
 * The Summary funnel's view-model helpers (docs/run-summary-funnel.md §10 step 2). Catalog-service
 * already does the heavy lifting server-side (decision A): `FunnelSummary.stages[]` arrives with
 * its chain-check (`check`) and reasons pre-computed, so this mapper only groups/formats for
 * render — it never re-derives a count the server already drew (principle 6, "one source per
 * number"). The single home for every pure (non-i18n) funnel computation; `funnel-resilience.ts`
 * holds the sibling helpers that need a `t` function to produce text.
 */
import {
  foldedStageStatus,
  funnelPaneStage,
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  PUBLISH_FOLD_TARGET,
  PUBLISH_STAGE,
  toApprovalStepCode,
  toImageCheckCode,
  toPublishStepCode,
} from '../constants/hidden-stages';
import type {
  FunnelBreakdownEntry,
  FunnelEvent,
  FunnelFact,
  FunnelReason,
  FunnelStage,
  FunnelStageDetail,
  FunnelStageReasons,
  FunnelSummary,
  FunnelTimeline,
} from '../types/run-funnel.api.types';

/** The funnel list's one shared bar scale (principle 7 / §4): Feed intake's `in`, so every bar shows
 *  shrinkage from the same 100%. Falls back to the first stage that reports an `in` for a
 *  flow/child-run that never reaches IMPORT. */
export function waterfallScale(stages: readonly FunnelStage[]): number {
  const first = stages.find((s) => s.in_ !== null);
  return first?.in_ ?? 0;
}

/** A count's share of the bar scale, 0-100, capped (a later stage never overflows the first bar). */
export function waterfallSharePercent(value: number | null | undefined, scale: number): number {
  if (value === null || value === undefined || scale <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / scale) * 100)));
}

/**
 * A stage row's "−N": everything that left the stage without becoming its `out` (excludes
 * `inFlight`, which hasn't resolved yet and is shown separately). `null` while the stage is still
 * running/waiting or hasn't been reached (resilience rule 2 — never a drop count before it closes).
 */
export function stageDropCount(stage: FunnelStage): number | null {
  if (isFunnelStageRunning(stage) || stage.status === 'NOT_REACHED') return null;
  return stage.excluded + stage.dropped + stage.failed + stage.notAttempted + stage.unexplained;
}

/** Resilience rule 1 — how long (ms) a running/waiting stage has gone with no `lastUpdateAt`
 *  movement; `null` once it closes or it has never reported one. Pairs with
 *  `funnel-resilience.ts`'s `silentStageNote`, which turns this into display text. */
export function silentForMs(stage: FunnelStage, now: Date = new Date()): number | null {
  if (!isFunnelStageRunning(stage) || !stage.lastUpdateAt) return null;
  return now.getTime() - new Date(stage.lastUpdateAt).getTime();
}

/** A stage counts as "still running" for resilience rule 2 ("never show ✘ before it closes") —
 *  a stage that already failed/was rejected is never "still running", even if its gate fields
 *  haven't caught up yet (status precedence: failed/rejected beats running/waiting). */
export function isFunnelStageRunning(stage: FunnelStage): boolean {
  if (isFunnelStageFailed(stage)) return false;
  return (
    stage.status === 'RUNNING' ||
    stage.status === 'AWAITING_GATE' ||
    isFunnelStageWaitingOnGate(stage)
  );
}

/** A stage is open on a gate decision the user hasn't made yet — the ONE real signal for "waiting
 *  on you" (amber hourglass), read straight from the gate object rather than `status` alone: a
 *  backend that marks the underlying work `COMPLETED` while its gate is still open would otherwise
 *  fall through to the terminal branches below and mis-paint as done/failed (the "Approval ·
 *  Waiting shows a red X" bug). A decided gate (`gate.outcome` set) always wins over a stale
 *  status. 2026-10-16 fix — "Approval · Waiting shows a green check": some deploys report the
 *  stage's own `WAITING`/`AWAITING_GATE` status without populating `gate` at all yet (the same
 *  contract-drift class the comment above already calls out), which used to fall all the way
 *  through to the terminal `doneOk` branch; trust that direct status signal too, but only while the
 *  gate hasn't actually been decided. */
export function isFunnelStageWaitingOnGate(stage: FunnelStage): boolean {
  if (stage.status === 'NOT_REACHED') return false;
  if (isFunnelGateDecided(stage)) return false;
  if (stage.gate?.required) return true;
  return stage.status === 'WAITING' || stage.status === 'AWAITING_GATE';
}

/** `gate.outcome` values that mean "still open, nobody has decided" — catalog-service's
 *  `/funnel/summary` reports an open gate as `outcome: "AWAITING_GATE"` (funnel_summary.py
 *  `_stage`), so a non-null outcome alone is NOT a decision (2026-10-16 "Approval shows a green
 *  check" root cause). */
const OPEN_GATE_OUTCOMES = new Set(['AWAITING_GATE', 'WAITING', 'PENDING', 'OPEN']);

/** True once a stage's gate carries a real decision (approved/completed, rejected, skipped) —
 *  `null` and the open-gate outcomes above never count as decided. */
export function isFunnelGateDecided(stage: FunnelStage): boolean {
  const outcome = stage.gate?.outcome;
  return outcome != null && !OPEN_GATE_OUTCOMES.has(outcome);
}

/** True when the stage's gate was decided as a rejection (either wire spelling) — the one check
 *  every "Rejected" label/tint reads, so no call site re-spells the two codes. */
export function isFunnelGateRejected(stage: FunnelStage): boolean {
  return stage.gate?.outcome === 'REJECTED' || stage.gate?.outcome === 'GATE_REJECT';
}

/** Everything that left the stage without becoming `out` (excluded, dropped, failed, not
 *  attempted, unexplained) — unlike `stageDropCount`, never `null` while the stage is open, so
 *  the Reason section can tell "nothing was lost" from "something was lost but not explained". */
export function stageLossTotal(stage: FunnelStage): number {
  return stage.excluded + stage.dropped + stage.failed + stage.notAttempted + stage.unexplained;
}

/** Statuses `FunnelStage.status` can report for a stage that stopped WITHOUT finishing cleanly
 *  (catalog-api-contract.md's `StageStatus` enum has no `REJECTED`, so a rejected gate still shows
 *  up as one of these). */
const FUNNEL_STAGE_FAILURE_STATUSES = new Set([
  'FAILED',
  'RECONCILE_FAILED',
  'CANCELLED',
  'TERMINATED',
]);

/** A stage's terminal state reads as a real failure/rejection — the ONE signal
 *  `funnelStatusGlyph`'s doneFail branch and the failure callouts key off, independent of the
 *  separate counts-balance (`check`) signal (principle: status/verdict drive the icon, `check`
 *  drives its own "counts add up" badge — the two must never be conflated, §10 rule). */
export function isFunnelStageFailed(stage: FunnelStage): boolean {
  if (isFunnelGateRejected(stage)) return true;
  if (!!stage.failure?.code || !!stage.failure?.reason) return true;
  return FUNNEL_STAGE_FAILURE_STATUSES.has(stage.status);
}

/** A stage is an optional one that soft-failed — render "skipped (soft failure)", never a plain
 *  drop (resilience rule 5). */
export function isSoftFailed(stage: FunnelStage): boolean {
  return !!stage.softFailure?.code || !!stage.softFailure?.reason;
}

/** True once every reached stage's chain-check passed — drives the ✔/✘ counts-add-up badge.
 *  `null` while the run is live (rule 2: never a check before the run / stage closes). */
export function funnelChainOk(summary: FunnelSummary): boolean | null {
  if (summary.check.ok !== null) return summary.check.ok;
  const reached = summary.stages.filter((s) => s.check !== null);
  if (reached.length === 0) return null;
  return reached.every((s) => s.check !== false);
}

/** ⑥ "vs last batch": a signed percent change, or `null` when either side is unknown or the
 *  previous value was 0 (a percent of zero is meaningless, not a divide-by-zero crash). */
export function pctChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

// ---------------------------------------------------------------------------
// Design C helpers — stage strip / funnel list navigation and "next N stages" collapse.
// ---------------------------------------------------------------------------

/** A stage has been reached once it has left `NOT_REACHED`, even if it later fails/skips. */
export function isStageReached(stage: FunnelStage): boolean {
  return stage.status !== 'NOT_REACHED';
}

/** Stages that measure rather than pass products along — "handed on to <next>" is meaningless
 *  for them (Insights reads the batch's scores; Approve & publish gets its products from content). */
const MEASURE_ONLY_STAGES: ReadonlySet<string> = new Set(['INSIGHTS']);

/** True when a stage hands its products on to the next stage (false for measure-only Insights). */
export function handsProductsOn(stageCode: string): boolean {
  return !MEASURE_ONLY_STAGES.has(stageCode);
}

/** `{index, total}` (1-based) of one stage within the flow template's full order — "stage 3 of
 *  10" on the stage page header. `null` when the stage code isn't in this run's template. */
export function stagePosition(
  stages: readonly FunnelStage[],
  stageCode: string,
): { index: number; total: number } | null {
  const index = stages.findIndex((s) => s.stage === stageCode);
  if (index === -1) return null;
  return { index: index + 1, total: stages.length };
}

/** Splits the template's stages into the ones already reached and the ones still ahead, in
 *  order — the funnel list renders the first in full and collapses the second into one line. */
export function splitReachedStages(stages: readonly FunnelStage[]): {
  reached: FunnelStage[];
  upcoming: FunnelStage[];
} {
  const firstUpcoming = stages.findIndex((s) => !isStageReached(s));
  if (firstUpcoming === -1) return { reached: [...stages], upcoming: [] };
  return { reached: stages.slice(0, firstUpcoming), upcoming: stages.slice(firstUpcoming) };
}

/** The elapsed time between an ISO instant and `now` in ms — `null` when `since` is missing.
 *  `now` defaults to the real clock so call sites never need their own `new Date()`. */
export function elapsedMsSince(since: string | null, now: Date = new Date()): number | null {
  if (!since) return null;
  const started = new Date(since).getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, now.getTime() - started);
}

/** The focus card's subject stage: the one `verdict.stage` names, falling back to the first
 *  running/waiting stage, then the last reached stage — a verdict always has something to point at. */
export function focusStage(summary: FunnelSummary): FunnelStage | null {
  const byVerdict = summary.verdict.stage
    ? summary.stages.find((s) => s.stage === summary.verdict.stage)
    : undefined;
  if (byVerdict) return byVerdict;
  const active = summary.stages.find(isFunnelStageRunning);
  if (active) return active;
  const reached = splitReachedStages(summary.stages).reached;
  return reached[reached.length - 1] ?? null;
}

/** The stage immediately after `stage` in the template order — what a gate's approval sends
 *  its output on to ("Approving sends 20 to Content score"). `null` at the last stage. */
export function nextStageAfter(
  stages: readonly FunnelStage[],
  stage: FunnelStage,
): FunnelStage | null {
  const index = stages.findIndex((s) => s.stage === stage.stage);
  if (index === -1 || index === stages.length - 1) return null;
  return stages[index + 1] ?? null;
}

/** A stage's "not a loss" topReasons (folded into another market, held back and since cleared,
 *  …) vs its real drops — split so the UI can show the fold-back fact without colouring it as
 *  an error the way a genuine drop reads. */
export function partitionTopReasons(
  stage: FunnelStage,
  isLoss: (code: string) => boolean,
): { losses: FunnelReason[]; notLosses: FunnelReason[] } {
  const losses: FunnelReason[] = [];
  const notLosses: FunnelReason[] = [];
  // A reason that removed nothing (e.g. a filter that excluded 0) explains no drop: listing it
  // read "−0 Excluded by filter: channel" in the stage rail.
  for (const reason of stage.topReasons) {
    if (reason.count <= 0) continue;
    (isLoss(reason.code) ? losses : notLosses).push(reason);
  }
  return { losses, notLosses };
}

/** The batch's biggest single drop across every reached stage — "Biggest drop: Matching ·
 *  image too small (2)" in the So-far footer. `null` when nothing has dropped yet. */
export function biggestDrop(
  summary: FunnelSummary,
): { stage: FunnelStage; reason: FunnelReason } | null {
  let best: { stage: FunnelStage; reason: FunnelReason } | null = null;
  for (const stage of splitReachedStages(summary.stages).reached) {
    for (const reason of stage.topReasons) {
      if (!best || reason.count > best.reason.count) best = { stage, reason };
    }
  }
  return best;
}

/** The default stage the mix opens on (§4 behaviour): the one the verdict names (needs the
 *  user), else the running one, else the last reached — `focusStage` already encodes exactly
 *  this order, so the stage pane's default selection and the focus band's subject never disagree. */
export const defaultSelectedStage = focusStage;

/**
 * A reason's `kind` (①/② `FunnelReason.kind`, the all-issues section's "Show products ›") mapped
 * onto ⑨'s matrix `outcome` filter — a best-effort coarsening, since the product matrix endpoint
 * filters by outcome STATUS, not by the exact reason CODE (funnel-v2-contract.md has no
 * `reason=` param on `GET …/funnel/products`). `null` clears the filter (show every outcome).
 */
export function reasonKindToMatrixOutcome(
  kind: string,
): 'PASSED' | 'DROPPED' | 'FAILED' | 'HELD_BACK' | null {
  switch (kind) {
    case 'FAILED':
      return 'FAILED';
    case 'HELD_BACK':
      return 'HELD_BACK';
    case 'FOLDED':
      return 'PASSED';
    case 'EXCLUDED':
    case 'DROPPED':
    case 'NOT_ATTEMPTED':
    case 'SKIPPED':
    case 'UNEXPLAINED':
      return 'DROPPED';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Hidden Image check stage (product decision 2026-10-16) — the ONE place IMAGE_GATE leaves the
// funnel. Every Summary surface (rail, pane, KPI row, focus card, all-issues, "stage N of M",
// drill targets, worst cases) reads `summary.stages`, so filtering here hides it everywhere.
// ---------------------------------------------------------------------------

/** Both checks must hold AND the two stages must hand off exactly (target out = hidden in);
 *  `null` while either is still open (rule 2 — never a check before a stage closes). */
function foldedCheck(
  targetCheck: boolean | null,
  hiddenCheck: boolean | null,
  handoffOk: boolean,
): boolean | null {
  if (targetCheck === null || hiddenCheck === null) return null;
  return targetCheck && hiddenCheck && handoffOk;
}

/** The later of two ISO instants (either may be missing). */
function laterIso(a: string | null, b: string | null): string | null {
  if (!a || !b) return a ?? b;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

/** A fold's reason-code prefixer (`toImageCheckCode`, `toApprovalStepCode`, `toPublishStepCode`). */
type CodePrefixer = (code: string | null, kind: string) => string;

/** A folded reason: same count/kind/flags, its code relabelled under a fold prefix ("Images · …"
 *  by default). */
function foldReason<T extends { code: string | null; kind: string }>(
  reason: T,
  prefix: CodePrefixer = toImageCheckCode,
): T & { code: string } {
  return { ...reason, code: prefix(reason.code, reason.kind) };
}

/**
 * CONSERVATION — Image check is hidden, but nothing it lost may disappear. Its losses are folded
 * into Feed intake: IMPORT keeps its `in`, takes Image check's `out`, adds every loss bucket
 * (excluded / dropped / failed / not attempted / in flight / unexplained) and appends its reasons
 * as "Images · …". So IMPORT still balances (in = out + losses) and the next stage's `in`
 * still equals IMPORT's `out` — the rail, pane and KPI numbers keep chaining with one stage fewer.
 * A lossless Image check (in = out, nothing dropped) makes this a no-op on the counts.
 */
function foldImageCheckIntoImport(target: FunnelStage, hidden: FunnelStage): FunnelStage {
  return {
    ...target,
    status: foldedStageStatus(target.status, hidden.status),
    out: hidden.out,
    excluded: target.excluded + hidden.excluded,
    dropped: target.dropped + hidden.dropped,
    failed: target.failed + hidden.failed,
    notAttempted: target.notAttempted + hidden.notAttempted,
    inFlight: target.inFlight + hidden.inFlight,
    unexplained: target.unexplained + hidden.unexplained,
    check: foldedCheck(target.check, hidden.check, target.out === hidden.in_),
    topReasons: [...target.topReasons, ...hidden.topReasons.map((r) => foldReason(r))],
    reasonsTotal: target.reasonsTotal + hidden.reasonsTotal,
    failure: target.failure ?? hidden.failure,
    endedAt: hidden.endedAt ?? (hidden.status === 'COMPLETED' ? target.endedAt : null),
    durationMs:
      target.durationMs !== null && hidden.durationMs !== null
        ? target.durationMs + hidden.durationMs
        : target.durationMs,
    lastUpdateAt: laterIso(target.lastUpdateAt, hidden.lastUpdateAt),
  };
}

/** A stage reference that pointed at the hidden stage now points at the one it folded into. */
function remapHiddenStage(stage: string): string {
  return stage === IMAGE_CHECK_STAGE ? IMAGE_CHECK_FOLD_TARGET : stage;
}

/**
 * ① with Image check removed: IMAGE_GATE dropped from `stages` (its losses folded into IMPORT once
 * it was reached) and every verdict / gate / needs-you reference to it re-pointed at IMPORT, so a
 * `?fStage=IMAGE_GATE` deep link or a verdict about it lands on Feed intake, never a missing pane.
 */
export function hideImageCheckStage(summary: FunnelSummary): FunnelSummary {
  const hidden = summary.stages.find((s) => s.stage === IMAGE_CHECK_STAGE);
  if (!hidden) return summary;
  const stages = summary.stages
    .filter((s) => s.stage !== IMAGE_CHECK_STAGE)
    .map((s) =>
      s.stage === IMAGE_CHECK_FOLD_TARGET && isStageReached(hidden)
        ? foldImageCheckIntoImport(s, hidden)
        : s,
    );
  const { verdict, cards } = summary;
  return {
    ...summary,
    stages,
    verdict: {
      ...verdict,
      stage: verdict.stage ? remapHiddenStage(verdict.stage) : null,
      gate: verdict.gate ? { ...verdict.gate, stage: remapHiddenStage(verdict.gate.stage) } : null,
    },
    cards: {
      ...cards,
      needsYou: cards.needsYou.map((a) => ({ ...a, stage: remapHiddenStage(a.stage) })),
    },
  };
}

/** Feed intake's ⑦ breakdown with Image check's folded in: both stages' loss rows (Image check's
 *  prefixed), then ONE `OUT` row carrying Image check's out — what Feed intake now hands on. */
function foldBreakdown(
  target: FunnelStageDetail,
  hidden: FunnelStageDetail,
): FunnelBreakdownEntry[] {
  const losses = (entries: FunnelBreakdownEntry[]) => entries.filter((e) => e.kind !== 'OUT');
  const hiddenOut = hidden.breakdown.filter((e) => e.kind === 'OUT');
  const out =
    hiddenOut.length > 0
      ? hiddenOut
      : target.breakdown
          .filter((e) => e.kind === 'OUT')
          .map((e) => ({ ...e, count: hidden.outCount ?? e.count }));
  return [
    ...losses(target.breakdown),
    ...losses(hidden.breakdown).map((e) => foldReason(e)),
    ...out,
  ];
}

/**
 * ⑦ Feed intake's detail with Image check's folded in (same conservation rule as
 * `hideImageCheckStage`): Image check's `outCount`, breakdown and reasons; facts, markets and
 * phases stay Feed intake's own (Image check's image facts are exactly what was removed).
 * `hidden` is `null`/not reached → Feed intake's detail unchanged.
 */
export function foldImageCheckDetail(
  target: FunnelStageDetail | null,
  hidden: FunnelStageDetail | null,
): FunnelStageDetail | null {
  if (!target || !hidden || hidden.status === 'NOT_REACHED') return target;
  return {
    ...target,
    status: foldedStageStatus(target.status, hidden.status),
    outCount: hidden.outCount,
    check: foldedCheck(target.check, hidden.check, target.outCount === hidden.inCount),
    endedAt: hidden.endedAt ?? (hidden.status === 'COMPLETED' ? target.endedAt : null),
    breakdown: foldBreakdown(target, hidden),
    reasons: [
      ...target.reasons,
      ...hidden.reasons.filter((r) => r.kind !== 'OUT').map((r) => foldReason(r)),
    ],
  };
}

/** ② Feed intake's reasons with Image check's appended ("Images · …") — the fallback Why
 *  rows must account for the same losses ⑦'s folded breakdown does. */
export function foldImageCheckReasons(
  target: FunnelStageReasons,
  hidden: FunnelStageReasons | null,
): FunnelStageReasons {
  if (!hidden || hidden.reasons.length === 0) return target;
  return {
    ...target,
    reasons: [...target.reasons, ...hidden.reasons.map((r) => foldReason(r))],
    total: target.total + hidden.total,
  };
}

// ---------------------------------------------------------------------------
// Approve & publish (product decision 2026-10-16) — the ONE place DELIVERY's funnel row folds
// into APPROVAL. Same pattern as Image check above: every Summary surface (rail, pane, KPI row,
// focus card, all-issues, "stage N of M", deep links) reads the folded `summary.stages`.
// ---------------------------------------------------------------------------

/** Verdict states in which the batch is still moving (an approved gate is about to hand over). */
const ACTIVE_RUN_STATES = new Set(['RUNNING', 'WAITING']);

/** True for the combined Approve & publish stage (carries its two raw steps). */
export function isPublishFold(stage: FunnelStage): boolean {
  return !!stage.steps;
}

/** The publish step a stage owns: DELIVERY itself, the combined stage's delivery step, else
 *  `null` — every "is this the publish pane" check reads this, never the code alone. */
export function publishStepOf(stage: FunnelStage): FunnelStage | null {
  if (stage.stage === PUBLISH_STAGE) return stage;
  return stage.steps?.delivery ?? null;
}

/** True once a stage's publish step has started — the delivery results/reads only mean
 *  something from then on (a plain DELIVERY stage keeps its own reads regardless). */
export function hasPublishStarted(stage: FunnelStage): boolean {
  if (stage.stage === PUBLISH_STAGE) return true;
  const delivery = stage.steps?.delivery;
  return !!delivery && isStageReached(delivery);
}

/** Approval's reason/breakdown rows relabelled "At approval: …" — except an open gate's
 *  IN_FLIGHT row, which must keep reading "waiting for your decision". */
function approvalRow<T extends { code: string | null; kind: string }>(row: T): T {
  return row.kind === 'IN_FLIGHT' ? row : foldReason(row, toApprovalStepCode);
}

/** Publish's reason/breakdown rows relabelled "At publish: …" (IN_FLIGHT kept as is). */
function publishRow<T extends { code: string | null; kind: string }>(row: T): T {
  return row.kind === 'IN_FLIGHT' ? row : foldReason(row, toPublishStepCode);
}

/**
 * The combined row's status, precedence failed (either step) > waiting (gate open) > running
 * (delivering) > completed (delivered) > not reached. An approved gate whose delivery hasn't
 * appeared yet reads RUNNING while the batch is still moving (the hand-off), never "Completed".
 */
function publishFoldStatus(approval: FunnelStage, delivery: FunnelStage, runActive: boolean) {
  if (isFunnelStageFailed(approval)) return approval.status;
  if (isFunnelStageFailed(delivery)) return delivery.status;
  if (isFunnelStageWaitingOnGate(approval)) return approval.status;
  if (isStageReached(delivery)) return delivery.status;
  if (approval.status === 'COMPLETED' && runActive) return 'RUNNING';
  return approval.status;
}

/**
 * CONSERVATION — APPROVAL and DELIVERY read as one stage, nothing either lost may disappear.
 * `in` = what reached approval; `out` = DELIVERY's out once delivery ran (else approval's own only
 * when it stopped there, else `null` — still waiting/handing over); every loss bucket is the sum of
 * both steps, each reason keeping its code under "At approval: …" / "At publish: …". Approval's
 * out IS delivery's in, so in = out + losses still holds and the previous stage's out still equals
 * this stage's in — the chain keeps balancing with one stage fewer.
 */
export function foldPublishIntoApproval(
  approval: FunnelStage,
  delivery: FunnelStage,
  runActive: boolean,
): FunnelStage {
  const delivered = isStageReached(delivery);
  const stoppedAtApproval = isFunnelStageFailed(approval);
  const both = (
    key: 'excluded' | 'dropped' | 'failed' | 'notAttempted' | 'inFlight' | 'unexplained',
  ) => approval[key] + (delivered ? delivery[key] : 0);
  return {
    ...approval,
    status: publishFoldStatus(approval, delivery, runActive),
    owner: isFunnelStageFailed(delivery) ? delivery.owner : approval.owner,
    out: delivered ? delivery.out : stoppedAtApproval ? approval.out : null,
    excluded: both('excluded'),
    dropped: both('dropped'),
    failed: both('failed'),
    notAttempted: both('notAttempted'),
    inFlight: both('inFlight'),
    unexplained: both('unexplained'),
    check: delivered
      ? foldedCheck(approval.check, delivery.check, approval.out === delivery.in_)
      : stoppedAtApproval
        ? approval.check
        : null,
    topReasons: [
      ...approval.topReasons.map(approvalRow),
      ...(delivered ? delivery.topReasons.map(publishRow) : []),
    ],
    reasonsTotal: approval.reasonsTotal + (delivered ? delivery.reasonsTotal : 0),
    failure: approval.failure ?? delivery.failure,
    softFailure: approval.softFailure ?? delivery.softFailure,
    endedAt: delivered ? delivery.endedAt : stoppedAtApproval ? approval.endedAt : null,
    durationMs:
      delivered && approval.durationMs !== null && delivery.durationMs !== null
        ? approval.durationMs + delivery.durationMs
        : approval.durationMs,
    lastUpdateAt: laterIso(approval.lastUpdateAt, delivery.lastUpdateAt),
    steps: { approval, delivery },
  };
}

/**
 * The funnel pane a `?fStage=` names, in the folded list: DELIVERY → the combined Approve &
 * publish stage (APPROVAL); APPROVAL on a flow without an approval gate → its plain DELIVERY;
 * otherwise the code itself. `null` when the list has no such stage (the default pane opens).
 */
export function resolvePaneStage(
  stages: readonly FunnelStage[],
  requested: string | null,
): string | null {
  if (!requested) return null;
  const has = (code: string) => stages.some((s) => s.stage === code);
  const candidates = [
    funnelPaneStage(requested),
    requested,
    requested === PUBLISH_FOLD_TARGET ? PUBLISH_STAGE : null,
  ];
  return candidates.find((code): code is string => !!code && has(code)) ?? null;
}

/** A stage reference that pointed at DELIVERY now points at the combined stage. */
function remapPublishStage(stage: string): string {
  return stage === PUBLISH_STAGE ? PUBLISH_FOLD_TARGET : stage;
}

/**
 * ① with Approval and Publish combined: DELIVERY dropped from `stages`, APPROVAL replaced by the
 * folded stage, and every verdict / gate / needs-you reference to DELIVERY re-pointed at APPROVAL.
 * A flow without both stages (no gate, or no destination) is returned unchanged.
 */
export function foldPublishStages(summary: FunnelSummary): FunnelSummary {
  const approval = summary.stages.find((s) => s.stage === PUBLISH_FOLD_TARGET);
  const delivery = summary.stages.find((s) => s.stage === PUBLISH_STAGE);
  if (!approval || !delivery) return summary;
  const runActive = ACTIVE_RUN_STATES.has(summary.verdict.state);
  const stages = summary.stages
    .filter((s) => s.stage !== PUBLISH_STAGE)
    .map((s) =>
      s.stage === PUBLISH_FOLD_TARGET ? foldPublishIntoApproval(s, delivery, runActive) : s,
    );
  const { verdict, cards } = summary;
  return {
    ...summary,
    stages,
    verdict: {
      ...verdict,
      stage: verdict.stage ? remapPublishStage(verdict.stage) : null,
      gate: verdict.gate ? { ...verdict.gate, stage: remapPublishStage(verdict.gate.stage) } : null,
    },
    cards: {
      ...cards,
      needsYou: cards.needsYou.map((a) => ({ ...a, stage: remapPublishStage(a.stage) })),
    },
  };
}

/** Facts from both steps, approval's first; a publish fact approval already states is dropped. */
function mergeFacts(
  approval: FunnelStageDetail['facts'],
  delivery: FunnelStageDetail['facts'],
): FunnelFact[] | null {
  if (!approval && !delivery) return null;
  const own = approval ?? [];
  const codes = new Set(own.map((f) => f.code));
  return [...own, ...(delivery ?? []).filter((f) => !codes.has(f.code))];
}

/** ⑦ statuses that mean the approval step stopped the batch (the out then is approval's own). */
const FAILED_DETAIL_STATUSES = new Set([
  'FAILED',
  'RECONCILE_FAILED',
  'CANCELLED',
  'TERMINATED',
  'REJECTED',
]);

/** True when ⑦'s approval step stopped the batch — a failure status or a rejected gate. */
function approvalDetailStopped(approval: FunnelStageDetail): boolean {
  const outcome = approval.gate?.outcome;
  return (
    FAILED_DETAIL_STATUSES.has(approval.status) ||
    outcome === 'REJECTED' ||
    outcome === 'GATE_REJECT'
  );
}

/**
 * ⑦ the combined stage's detail (same conservation rule as `foldPublishIntoApproval`): both
 * steps' loss rows and reasons ("At approval: …" / "At publish: …"), then the OUT row — delivery's
 * once it ran, approval's own until then; facts merged; gate, markets and phases stay approval's
 * (Publish's per-market results are ④'s, shown in the Products card). `delivery` `null` (a flow
 * without it) → approval's detail unchanged.
 */
export function foldPublishDetail(
  approval: FunnelStageDetail | null,
  delivery: FunnelStageDetail | null,
): FunnelStageDetail | null {
  if (!approval || !delivery) return approval;
  const delivered = delivery.status !== 'NOT_REACHED';
  const losses = (rows: FunnelBreakdownEntry[]) => rows.filter((e) => e.kind !== 'OUT');
  const outRows = (delivered ? delivery : approval).breakdown.filter((e) => e.kind === 'OUT');
  const stoppedAtApproval = approvalDetailStopped(approval);
  return {
    ...approval,
    status: delivered && !stoppedAtApproval ? delivery.status : approval.status,
    outCount: delivered ? delivery.outCount : stoppedAtApproval ? approval.outCount : null,
    check: delivered
      ? foldedCheck(approval.check, delivery.check, approval.outCount === delivery.inCount)
      : stoppedAtApproval
        ? approval.check
        : null,
    endedAt: delivered ? delivery.endedAt : stoppedAtApproval ? approval.endedAt : null,
    durationMs:
      delivered && approval.durationMs !== null && delivery.durationMs !== null
        ? approval.durationMs + delivery.durationMs
        : approval.durationMs,
    breakdown: [
      ...losses(approval.breakdown).map(approvalRow),
      ...(delivered ? losses(delivery.breakdown).map(publishRow) : []),
      ...outRows,
    ],
    reasons: [
      ...approval.reasons.filter((r) => r.kind !== 'OUT').map(approvalRow),
      ...(delivered ? delivery.reasons.filter((r) => r.kind !== 'OUT').map(publishRow) : []),
    ],
    facts: mergeFacts(approval.facts, delivered ? delivery.facts : null),
    productsSource: [...approval.productsSource, ...delivery.productsSource],
    unavailable: [...new Set([...approval.unavailable, ...delivery.unavailable])],
  };
}

/** ② the combined stage's reasons — approval's ("At approval: …"), then publish's ("At
 *  publish: …"), so the fallback Why rows account for the same losses ⑦'s folded breakdown does. */
export function foldPublishReasons(
  approval: FunnelStageReasons,
  delivery: FunnelStageReasons | null,
): FunnelStageReasons {
  if (!delivery) return approval;
  return {
    ...approval,
    reasons: [...approval.reasons.map(approvalRow), ...delivery.reasons.map(publishRow)],
    total: approval.total + delivery.total,
  };
}

/** ⑧ the combined stage's timeline: both steps' events merged oldest-first (gate opened →
 *  decided → sent → live check). Either side `null` (not deployed / no such stage) → the other. */
export function foldPublishTimeline(
  approval: FunnelTimeline | null,
  delivery: FunnelTimeline | null,
): FunnelTimeline | null {
  if (!approval || !delivery) return approval ?? delivery;
  const at = (e: FunnelEvent) => new Date(e.at).getTime();
  return {
    events: [...approval.events, ...delivery.events].sort((a, b) => at(a) - at(b)),
    unavailable: [...new Set([...approval.unavailable, ...delivery.unavailable])],
  };
}
